import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Home, GripVertical, TrendingUp, Archive, Loader2, Copy, RefreshCw } from 'lucide-react';
import * as cacheManager from '../utils/cacheManager';

const Reports = ({ properties = [], preloaded = false }) => {
  // Core state
  const [monthOffset, setMonthOffset] = useState(0);
  const [slots, setSlots] = useState([]); // ReportSlot objects from MongoDB
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track loaded range for mini-sync
  const [loadedRange, setLoadedRange] = useState({ min: -12, max: 12 });
  const [isMiniSyncing, setIsMiniSyncing] = useState(false);

  // Year to date overlay
  const [showYTD, setShowYTD] = useState(false);
  const [ytdData, setYtdData] = useState(null);

  // Cost form states
  const [showCostForm, setShowCostForm] = useState(null);
  const [newCost, setNewCost] = useState({ name: '', amount: '', isRecurring: false });
  const [editingCost, setEditingCost] = useState(null);

  // YTD per-property breakdown toggle
  const [showYTDProperties, setShowYTDProperties] = useState(false);

  // Copy settings state
  const [isCopying, setIsCopying] = useState(false);
  const [copyMessage, setCopyMessage] = useState(null);

  // Archive toggle state
  const [showArchived, setShowArchived] = useState(false);

  // Global costs (stored separately in a "TOTALS" slot or managed locally)
  const [globalCosts, setGlobalCosts] = useState([]);

  // Date helpers
  const getMonthDateRange = useCallback((offset = 0) => {
    const today = new Date();
    const targetDate = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const startDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
    endDate.setHours(23, 59, 59, 999);
    return { startDate, endDate, targetDate };
  }, []);

  const isMonthCompleted = useCallback((offset) => {
    const { endDate } = getMonthDateRange(offset);
    return endDate < new Date();
  }, [getMonthDateRange]);

  const formatDate = (offset) => {
    const { targetDate } = getMonthDateRange(offset);
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long' }).format(targetDate);
  };

  const formatNumber = (num) =>
    (Number(num) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Recalculate net income locally (for optimistic updates)
  const recalcNetIncome = useCallback((slot, costs) => {
    const totalCosts = (costs || []).reduce((sum, cost) => sum + (Number(cost.amount) || 0), 0);
    if (slot.isAdministrated) {
      const adminFee = Math.max(slot.revenue - slot.cleaningFees, 0) * 0.1;
      return adminFee - totalCosts;
    }
    return slot.revenue - slot.cleaningFees - totalCosts;
  }, []);

  // Fetch fresh data from API
  const fetchFreshReportSlots = useCallback(async (offset) => {
    const monthKey = cacheManager.getMonthKey(offset);
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reservations/report-slots?startMonth=${monthKey}&endMonth=${monthKey}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch report slots: ${response.status}`);
      }

      const data = await response.json();
      const monthSlots = data.slots[monthKey] || [];

      // Sort by order
      monthSlots.sort((a, b) => (a.order || 0) - (b.order || 0));

      // Cache to localStorage
      cacheManager.setReportSlots(monthKey, monthSlots);

      // Update state
      setSlots(monthSlots);

      // Load global costs from localStorage (these are managed client-side)
      const globalCostsKey = `reports_global_costs_${monthKey}`;
      const storedGlobalCosts = localStorage.getItem(globalCostsKey);
      if (storedGlobalCosts) {
        setGlobalCosts(JSON.parse(storedGlobalCosts));
      } else {
        setGlobalCosts([]);
      }

      console.log(`[Reports] Loaded ${monthSlots.length} slots for ${monthKey}`);

    } catch (err) {
      console.error('[Reports] Error fetching slots:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Trigger mini-sync for months beyond pre-computed range
  const triggerMiniSync = useCallback(async (targetOffset) => {
    setIsMiniSyncing(true);
    setLoading(true);

    try {
      const direction = targetOffset > loadedRange.max ? 1 : -1;
      const startOffset = direction > 0 ? loadedRange.max + 1 : loadedRange.min - 6;
      const endOffset = direction > 0 ? loadedRange.max + 6 : loadedRange.min - 1;

      const startMonth = cacheManager.getMonthKey(startOffset);
      const endMonth = cacheManager.getMonthKey(endOffset);

      console.log(`[Reports] Mini-sync triggered for ${startMonth} to ${endMonth}`);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reservations/mini-sync?startMonth=${startMonth}&endMonth=${endMonth}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );

      if (!response.ok) {
        throw new Error('Mini-sync failed');
      }

      const data = await response.json();

      // Cache the new slots by month
      if (data.slots) {
        Object.keys(data.slots).forEach(monthKey => {
          cacheManager.setReportSlots(monthKey, data.slots[monthKey]);
        });
      }

      // Update loaded range
      setLoadedRange(prev => ({
        min: Math.min(prev.min, startOffset),
        max: Math.max(prev.max, endOffset)
      }));

      // Now load the requested month
      await fetchFreshReportSlots(targetOffset);

    } catch (err) {
      console.error('[Reports] Mini-sync error:', err);
      setError('Failed to load data for this month. Try syncing manually.');
    } finally {
      setIsMiniSyncing(false);
      setLoading(false);
    }
  }, [loadedRange, fetchFreshReportSlots]);

  // Load report slots from cache or API
  const loadReportSlots = useCallback(async (offset, forceRefresh = false) => {
    const monthKey = cacheManager.getMonthKey(offset);

    // Check if beyond pre-computed range and needs mini-sync
    if (offset > loadedRange.max || offset < loadedRange.min) {
      await triggerMiniSync(offset);
      return;
    }

    // 1. Check localStorage first (instant display)
    if (!forceRefresh) {
      const cached = cacheManager.getReportSlots(monthKey);
      if (cached && cached.slots && cacheManager.isCacheValid(cached.timestamp)) {
        console.log(`[Reports] Using cached data for ${monthKey}`);
        setSlots(cached.slots);

        // Load global costs from localStorage
        const globalCostsKey = `reports_global_costs_${monthKey}`;
        const storedGlobalCosts = localStorage.getItem(globalCostsKey);
        if (storedGlobalCosts) {
          setGlobalCosts(JSON.parse(storedGlobalCosts));
        } else {
          setGlobalCosts([]);
        }
        return;
      }
    }

    // 2. Fetch from MongoDB
    await fetchFreshReportSlots(offset);
  }, [loadedRange, fetchFreshReportSlots, triggerMiniSync]);



  // Handle cost changes (optimistic update + async save)
  const handleAddCost = async (target, cost) => {
    if (!cost.name?.trim() || !cost.amount) return;

    const cleanedCost = {
      name: cost.name.trim(),
      amount: Number(cost.amount),
      isRecurring: !!cost.isRecurring
    };

    const monthKey = cacheManager.getMonthKey(monthOffset);

    if (target === 'totals') {
      // Global costs - managed client-side
      const existingNames = new Set(globalCosts.map(c => c.name.toLowerCase().trim()));
      if (!existingNames.has(cleanedCost.name.toLowerCase())) {
        const newGlobalCosts = [...globalCosts, cleanedCost];
        setGlobalCosts(newGlobalCosts);
        localStorage.setItem(`reports_global_costs_${monthKey}`, JSON.stringify(newGlobalCosts));
      }

      // Recurring global costs: copy into the next 12 months (dedup by name)
      if (cleanedCost.isRecurring) {
        for (let i = 1; i <= 12; i++) {
          const futureKey = `reports_global_costs_${cacheManager.getMonthKey(monthOffset + i)}`;
          let futureCosts = [];
          try {
            futureCosts = JSON.parse(localStorage.getItem(futureKey) || '[]');
          } catch (e) { /* corrupted entry — overwrite */ }
          const names = new Set(futureCosts.map(c => c.name.toLowerCase().trim()));
          if (!names.has(cleanedCost.name.toLowerCase())) {
            futureCosts.push(cleanedCost);
            localStorage.setItem(futureKey, JSON.stringify(futureCosts));
          }
        }
      }
    } else {
      // Property cost - update slot
      const slot = slots.find(s => s._id === target);
      if (!slot) return;

      const existingNames = new Set((slot.costs || []).map(c => c.name.toLowerCase().trim()));
      if (existingNames.has(cleanedCost.name.toLowerCase())) return;

      const newCosts = [...(slot.costs || []), cleanedCost];
      const newNetIncome = recalcNetIncome(slot, newCosts);

      // 1. Update state immediately (optimistic)
      setSlots(prev => prev.map(s =>
        s._id === target ? { ...s, costs: newCosts, netIncome: newNetIncome } : s
      ));

      // 2. Update localStorage
      cacheManager.updateReportSlot(monthKey, target, { costs: newCosts, netIncome: newNetIncome });

      // 3. Save to MongoDB (async, don't await for UI). The server copies a
      // newly added recurring cost into future months and reports which —
      // clear those months' caches so they refetch with the new cost.
      fetch(`${process.env.REACT_APP_API_URL}/api/reservations/report-slots/${target}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ costs: newCosts })
      }).then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        (data.propagatedTo || []).forEach(mk => cacheManager.clearReportSlots(mk));
      }).catch(err => {
        console.error('[Reports] Error saving cost:', err);
        alert('Failed to save cost — it may be lost on refresh.');
      });
    }

    setNewCost({ name: '', amount: '', isRecurring: false });
    setShowCostForm(null);
  };

  const handleDeleteCost = async (target, index) => {
    const monthKey = cacheManager.getMonthKey(monthOffset);

    if (target === 'totals') {
      const newGlobalCosts = globalCosts.filter((_, i) => i !== index);
      setGlobalCosts(newGlobalCosts);
      localStorage.setItem(`reports_global_costs_${monthKey}`, JSON.stringify(newGlobalCosts));
    } else {
      const slot = slots.find(s => s._id === target);
      if (!slot) return;

      const newCosts = (slot.costs || []).filter((_, i) => i !== index);
      const newNetIncome = recalcNetIncome(slot, newCosts);

      // Optimistic update
      setSlots(prev => prev.map(s =>
        s._id === target ? { ...s, costs: newCosts, netIncome: newNetIncome } : s
      ));

      cacheManager.updateReportSlot(monthKey, target, { costs: newCosts, netIncome: newNetIncome });

      fetch(`${process.env.REACT_APP_API_URL}/api/reservations/report-slots/${target}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ costs: newCosts })
      }).catch(err => console.error('[Reports] Error deleting cost:', err));
    }
  };

  // Admin toggle
  const handleAdminToggle = async (slotId, isAdministrated) => {
    const monthKey = cacheManager.getMonthKey(monthOffset);
    const slot = slots.find(s => s._id === slotId);
    if (!slot) return;

    const newNetIncome = recalcNetIncome({ ...slot, isAdministrated }, slot.costs);

    // Optimistic update
    setSlots(prev => prev.map(s =>
      s._id === slotId ? { ...s, isAdministrated, netIncome: newNetIncome } : s
    ));

    cacheManager.updateReportSlot(monthKey, slotId, { isAdministrated, netIncome: newNetIncome });

    fetch(`${process.env.REACT_APP_API_URL}/api/reservations/report-slots/${slotId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify({ isAdministrated })
    }).catch(err => console.error('[Reports] Error saving admin toggle:', err));
  };

  // Copy settings to next or previous month
  const handleCopySettings = async (direction) => {
    const targetOffset = direction === 'next' ? monthOffset + 1 : monthOffset - 1;
    const sourceMonthKey = cacheManager.getMonthKey(monthOffset);
    const targetMonthKey = cacheManager.getMonthKey(targetOffset);

    setIsCopying(true);
    setCopyMessage(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reservations/copy-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ sourceMonth: sourceMonthKey, targetMonth: targetMonthKey })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Copy failed');
      }

      // Copy global costs client-side
      const sourceGlobalCosts = localStorage.getItem(`reports_global_costs_${sourceMonthKey}`);
      if (sourceGlobalCosts) {
        localStorage.setItem(`reports_global_costs_${targetMonthKey}`, sourceGlobalCosts);
      } else {
        localStorage.removeItem(`reports_global_costs_${targetMonthKey}`);
      }

      // Invalidate target month cache so next visit fetches fresh data
      cacheManager.clearReportSlots(targetMonthKey);

      setCopyMessage({ type: 'success', text: data.message });
    } catch (err) {
      setCopyMessage({ type: 'error', text: err.message });
    } finally {
      setIsCopying(false);
      setTimeout(() => setCopyMessage(null), 4000);
    }
  };

  // Navigation — just update the offset; the load effect below is the
  // single fetch path (calling loadReportSlots here too caused a duplicate
  // fetch per navigation and could fire two mini-syncs concurrently)
  const navigateMonth = (newOffset) => {
    setMonthOffset(newOffset);
  };

  // Fetch Year-to-Date data — January through the viewed month of the viewed
  // year, straight from the API (the old cache-walk silently skipped any
  // month the user hadn't visited, so totals depended on browsing history)
  const fetchYTDData = useCallback(async (currentOffset) => {
    try {
      const { targetDate } = getMonthDateRange(currentOffset);
      const targetYear = targetDate.getFullYear();
      const startMonth = `${targetYear}-01`;
      const endMonth = `${targetYear}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reservations/report-slots?startMonth=${startMonth}&endMonth=${endMonth}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch YTD slots: ${response.status}`);
      }
      const data = await response.json();
      const byMonth = data.slots || {};

      let totalRevenue = 0;
      let totalProfit = 0;
      let monthsProcessed = 0;
      const perProperty = new Map();

      Object.keys(byMonth).sort().forEach(monthKey => {
        const monthSlots = byMonth[monthKey] || [];
        if (monthSlots.length === 0) return;
        monthsProcessed++;

        let monthProfit = 0;
        monthSlots.forEach(slot => {
          totalRevenue += slot.revenue || 0;
          monthProfit += slot.netIncome || 0;

          const key = slot.propertyId || slot.propertyName;
          const entry = perProperty.get(key) || {
            name: slot.propertyName, revenue: 0, profit: 0, months: 0
          };
          entry.revenue += slot.revenue || 0;
          entry.profit += slot.netIncome || 0;
          entry.months++;
          perProperty.set(key, entry);
        });

        // Global costs live client-side per month — same convention as the
        // monthly Total Profit card
        const storedGlobalCosts = localStorage.getItem(`reports_global_costs_${monthKey}`);
        const monthGlobalCosts = storedGlobalCosts
          ? JSON.parse(storedGlobalCosts).reduce((sum, c) => sum + (c.amount || 0), 0)
          : 0;
        totalProfit += monthProfit - monthGlobalCosts;
      });

      // Per-property rows: costs follow the same convention as the summary
      // cards (revenue minus profit); averages are per month with data
      const propertyRows = [...perProperty.values()]
        .map(p => ({
          ...p,
          costs: p.revenue - p.profit,
          avgRevenue: p.months > 0 ? p.revenue / p.months : 0,
          avgProfit: p.months > 0 ? p.profit / p.months : 0
        }))
        .sort((a, b) => b.revenue - a.revenue);

      const totalCosts = totalRevenue - totalProfit;

      return {
        year: targetYear,
        totalRevenue,
        totalCosts,
        totalProfit,
        monthsProcessed,
        averageRevenue: monthsProcessed > 0 ? totalRevenue / monthsProcessed : 0,
        averageCosts: monthsProcessed > 0 ? totalCosts / monthsProcessed : 0,
        averageProfit: monthsProcessed > 0 ? totalProfit / monthsProcessed : 0,
        properties: propertyRows
      };
    } catch (error) {
      console.error('[Reports] Error fetching YTD data:', error);
      return null;
    }
  }, [getMonthDateRange]);

  // Toggle YTD overlay — the load effect fetches the data when it turns on
  const toggleYTD = () => {
    setShowYTD(prev => !prev);
  };

  // Calculations from pre-computed slots
  const totalRevenue = useMemo(() =>
    slots.reduce((sum, s) => sum + (s.revenue || 0), 0),
    [slots]
  );

  const totalCleaningFees = useMemo(() =>
    slots.reduce((sum, s) => sum + (s.cleaningFees || 0), 0),
    [slots]
  );

  const realTotalCosts = useMemo(() =>
    slots.reduce((sum, s) => sum + (s.costs || []).reduce((cs, c) => cs + (c.amount || 0), 0), 0),
    [slots]
  );

  const totalGlobalCosts = useMemo(() =>
    globalCosts.reduce((sum, c) => sum + (c.amount || 0), 0),
    [globalCosts]
  );

  // Revenue split
  const revenueSplit = useMemo(() => ({
    managed: slots.filter(s => s.isAdministrated).reduce((sum, s) => sum + (s.revenue || 0), 0),
    owned: slots.filter(s => !s.isAdministrated).reduce((sum, s) => sum + (s.revenue || 0), 0)
  }), [slots]);

  // Profit split
  const profitSplit = useMemo(() => {
    const managed = slots.filter(s => s.isAdministrated).reduce((sum, s) => sum + (s.netIncome || 0), 0);
    const ownedPropertyProfit = slots.filter(s => !s.isAdministrated).reduce((sum, s) => sum + (s.netIncome || 0), 0);
    return { managed, owned: ownedPropertyProfit - totalGlobalCosts };
  }, [slots, totalGlobalCosts]);

  const totalProfit = profitSplit.managed + profitSplit.owned;

  // Archive feature
  const isPropertyEmpty = useCallback((slot) => {
    const hasRevenue = (slot.revenue || 0) > 0;
    const hasCleaningFees = (slot.cleaningFees || 0) > 0;
    const hasCosts = (slot.costs || []).length > 0;
    return !hasRevenue && !hasCleaningFees && !hasCosts;
  }, []);

  const visibleSlots = useMemo(() => {
    if (showArchived) return slots;
    return slots.filter(slot => !isPropertyEmpty(slot));
  }, [slots, showArchived, isPropertyEmpty]);

  const archivedCount = useMemo(() =>
    slots.filter(slot => isPropertyEmpty(slot)).length,
    [slots, isPropertyEmpty]
  );

  // Load slots (and YTD when shown) on mount and whenever the month changes
  useEffect(() => {
    const load = async () => {
      await loadReportSlots(monthOffset);
      if (showYTD) {
        setYtdData(await fetchYTDData(monthOffset));
      }
    };
    load();
  }, [loadReportSlots, monthOffset, showYTD, fetchYTDData]);


  const isPastMonth = isMonthCompleted(monthOffset);
  const isCurrentMonth = monthOffset === 0;

  if (loading && slots.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-zinc-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500 mx-auto mb-2"></div>
          {isMiniSyncing && <p className="text-sm text-zinc-500">Syncing data for this month...</p>}
        </div>
      </div>
    );
  }

  if (error && slots.length === 0) {
    return (
      <div className="h-full flex flex-col bg-zinc-950">
        <div className="border-b border-zinc-800 p-4">
          <h2 className="text-base font-semibold text-zinc-100">Reports</h2>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-red-950/50 border border-red-800 rounded-xl p-4 max-w-lg">
            <p className="text-red-400 mb-2 font-medium text-sm">Error loading reports</p>
            <p className="text-zinc-400 text-sm mb-4">{error}</p>
            <button
              onClick={() => loadReportSlots(monthOffset, true)}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-950">
      {/* Header */}
      <div className="border-b border-zinc-800 p-4 flex justify-between items-center">
        <button
          onClick={() => navigateMonth(monthOffset - 1)}
          disabled={loading}
          className={`p-2 rounded-lg transition-colors ${loading ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'}`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="text-center">
          <h2 className="text-xl font-semibold text-zinc-100">{formatDate(monthOffset)}</h2>
          <div className="flex items-center justify-center gap-2 mt-1.5">
            {isPastMonth && <span className="text-xs bg-zinc-800 text-zinc-500 border border-zinc-700 px-2 py-0.5 rounded-md">Stored</span>}
            {isCurrentMonth && <span className="text-xs bg-emerald-900/30 text-emerald-400 border border-emerald-800 px-2 py-0.5 rounded-md">Current</span>}
            {!isPastMonth && !isCurrentMonth && (
              <span className="text-xs bg-violet-900/30 text-violet-400 border border-violet-800 px-2 py-0.5 rounded-md">Future</span>
            )}
            {monthOffset !== 0 && (
              <button onClick={() => navigateMonth(0)} className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1 transition-colors">
                <Home className="w-3 h-3" />
                Current Month
              </button>
            )}
            <button
              onClick={toggleYTD}
              className={`text-xs px-2 py-0.5 rounded-md flex items-center gap-1 border transition-colors ${
                showYTD ? 'bg-violet-600 text-white border-violet-600' : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700'
              }`}
            >
              <TrendingUp className="w-3 h-3" />
              YTD
            </button>
            {archivedCount > 0 && (
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={`text-xs px-2 py-0.5 rounded-md flex items-center gap-1 border transition-colors ${
                  showArchived ? 'bg-amber-600 text-white border-amber-600' : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700'
                }`}
              >
                <Archive className="w-3 h-3" />
                {showArchived ? 'Hide' : 'Show'} Archived ({archivedCount})
              </button>
            )}
            {(loading || isMiniSyncing) && (
              <span className="text-xs text-zinc-600 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                {isMiniSyncing ? 'Syncing...' : 'Updating...'}
              </span>
            )}
          </div>
          <div className="flex items-center justify-center gap-2 mt-2">
            <button
              onClick={() => handleCopySettings('prev')}
              disabled={isCopying || loading}
              className="text-xs px-2 py-0.5 rounded-md flex items-center gap-1 bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-700 disabled:opacity-50 transition-colors"
            >
              <Copy className="w-3 h-3" />
              Copy to prev month
            </button>
            <button
              onClick={() => handleCopySettings('next')}
              disabled={isCopying || loading}
              className="text-xs px-2 py-0.5 rounded-md flex items-center gap-1 bg-zinc-800 text-zinc-400 hover:bg-zinc-700 border border-zinc-700 disabled:opacity-50 transition-colors"
            >
              <Copy className="w-3 h-3" />
              Copy to next month
            </button>
            {isCopying && (
              <span className="text-xs text-zinc-600 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Copying...
              </span>
            )}
            {copyMessage && (
              <span className={`text-xs px-2 py-0.5 rounded-md border ${copyMessage.type === 'success' ? 'bg-emerald-900/30 text-emerald-400 border-emerald-800' : 'bg-red-950/50 text-red-400 border-red-800'}`}>
                {copyMessage.text}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={() => navigateMonth(monthOffset + 1)}
          disabled={loading}
          className={`p-2 rounded-lg transition-colors ${loading ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'}`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Year to Date Overlay */}
      {showYTD && ytdData && (
        <div className="bg-violet-950/20 border-b border-violet-900/50 p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-violet-300">
                Year to Date Summary{ytdData.year ? ` — ${ytdData.year}` : ''}
              </h3>
              {(ytdData.properties || []).length > 0 && (
                <button
                  onClick={() => setShowYTDProperties(prev => !prev)}
                  className={`text-xs px-2 py-0.5 rounded-md flex items-center gap-1 border transition-colors ${
                    showYTDProperties
                      ? 'bg-violet-600 text-white border-violet-600'
                      : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700'
                  }`}
                >
                  <Home className="w-3 h-3" />
                  By Property
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                <div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Total Revenue</div>
                <div className="text-base font-bold text-emerald-400">${formatNumber(ytdData.totalRevenue)}</div>
                <div className="text-xs text-zinc-600">Avg: ${formatNumber(ytdData.averageRevenue)}</div>
              </div>
              <div className="bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                <div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Total Costs</div>
                <div className="text-base font-bold text-red-400">${formatNumber(ytdData.totalCosts)}</div>
                <div className="text-xs text-zinc-600">Avg: ${formatNumber(ytdData.averageCosts)}</div>
              </div>
              <div className="bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                <div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Total Profit</div>
                <div className={`text-base font-bold ${ytdData.totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  ${formatNumber(ytdData.totalProfit)}
                </div>
                <div className="text-xs text-zinc-600">Avg: ${formatNumber(ytdData.averageProfit)}</div>
              </div>
              <div className="bg-zinc-800 rounded-lg p-3 border border-zinc-700">
                <div className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Months</div>
                <div className="text-base font-bold text-violet-400">{ytdData.monthsProcessed}</div>
                <div className="text-xs text-zinc-600">Processed</div>
              </div>
            </div>

            {/* Per-property YTD breakdown */}
            {showYTDProperties && (ytdData.properties || []).length > 0 && (
              <div className="mt-3 bg-zinc-800 rounded-lg border border-zinc-700 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-zinc-500 uppercase tracking-wide border-b border-zinc-700">
                      <th className="text-left font-medium px-3 py-2">Property</th>
                      <th className="text-right font-medium px-3 py-2">Revenue</th>
                      <th className="text-right font-medium px-3 py-2">Avg Rev/mo</th>
                      <th className="text-right font-medium px-3 py-2">Costs</th>
                      <th className="text-right font-medium px-3 py-2">Profit</th>
                      <th className="text-right font-medium px-3 py-2">Avg Profit/mo</th>
                      <th className="text-right font-medium px-3 py-2">Months</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ytdData.properties.map((p, i) => (
                      <tr key={p.name + i} className="border-b border-zinc-700/50 last:border-0">
                        <td className="px-3 py-2 text-zinc-200 font-medium whitespace-nowrap">{p.name}</td>
                        <td className="px-3 py-2 text-right text-emerald-400">${formatNumber(p.revenue)}</td>
                        <td className="px-3 py-2 text-right text-zinc-400">${formatNumber(p.avgRevenue)}</td>
                        <td className="px-3 py-2 text-right text-red-400">${formatNumber(p.costs)}</td>
                        <td className={`px-3 py-2 text-right font-medium ${p.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          ${formatNumber(p.profit)}
                        </td>
                        <td className={`px-3 py-2 text-right ${p.avgProfit >= 0 ? 'text-zinc-400' : 'text-red-400'}`}>
                          ${formatNumber(p.avgProfit)}
                        </td>
                        <td className="px-3 py-2 text-right text-zinc-500">{p.months}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-3 py-2 text-[11px] text-zinc-600 border-t border-zinc-700">
                  Costs = revenue − profit (same convention as the summary cards); averages are per month with data. Global costs are not split per property.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Totals Card */}
          <div className="bg-zinc-800 rounded-xl border border-zinc-700">
            <div className="p-4 border-b border-zinc-700">
              <h3 className="text-sm font-semibold text-zinc-100">Monthly Totals</h3>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-300">Total Revenue</span>
                <span className="text-sm font-medium text-zinc-100">${formatNumber(totalRevenue)}</span>
              </div>
              <div className="pl-4 text-xs space-y-1">
                <div className="flex justify-between items-center text-zinc-600">
                  <span>Managed Revenue</span>
                  <span>${formatNumber(revenueSplit.managed)}</span>
                </div>
                <div className="flex justify-between items-center text-zinc-600">
                  <span>Owned Revenue</span>
                  <span>${formatNumber(revenueSplit.owned)}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-300">Total Cleaning Fees</span>
                <span className="text-sm font-medium text-zinc-100">${formatNumber(totalCleaningFees)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-300">Total Property Costs</span>
                <span className="text-sm font-medium text-zinc-100">${formatNumber(realTotalCosts)}</span>
              </div>

              <div className="border-t border-zinc-700 pt-3">
                <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">Global Costs</h4>

                {globalCosts.map((cost, i) => (
                  <div key={`total-${i}`} className="flex items-center gap-2 mb-2">
                    <span className="flex-1 text-sm text-zinc-300 flex items-center gap-1.5">
                      {cost.name}
                      {cost.isRecurring && (
                        <RefreshCw className="w-3 h-3 text-violet-400" title="Recurring — copied to future months" />
                      )}
                    </span>
                    <span className="w-24 text-right text-sm font-medium text-zinc-300">
                      ${formatNumber(cost.amount)}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDeleteCost('totals', i)}
                        className="text-zinc-600 hover:text-red-400 transition-colors"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {showCostForm === 'totals' ? (
                  <div className="space-y-2 p-3 bg-zinc-950 rounded-lg border border-zinc-800 mt-2">
                    <input
                      type="text"
                      value={newCost.name}
                      onChange={(e) => setNewCost(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Cost description"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-md py-1.5 px-2 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 text-sm"
                      disabled={loading}
                    />
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        value={newCost.amount}
                        onChange={(e) => setNewCost(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="Amount"
                        className="w-28 bg-zinc-800 border border-zinc-700 rounded-md py-1.5 px-2 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 text-sm"
                        disabled={loading}
                        step="0.01"
                      />
                      <label className="flex items-center gap-1.5 text-xs text-zinc-400 whitespace-nowrap cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newCost.isRecurring}
                          onChange={(e) => setNewCost(prev => ({ ...prev, isRecurring: e.target.checked }))}
                          className="rounded border-zinc-600 text-violet-600 focus:ring-violet-500 bg-zinc-800"
                          disabled={loading}
                        />
                        Recurring
                      </label>
                      <div className="flex-1" />
                      <button
                        onClick={() => handleAddCost('totals', newCost)}
                        className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-sm hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                        disabled={loading || !newCost.name || !newCost.amount}
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setShowCostForm(null);
                          setNewCost({ name: '', amount: '', isRecurring: false });
                        }}
                        className="px-3 py-1.5 bg-zinc-800 text-zinc-400 rounded-md text-sm hover:bg-zinc-700 border border-zinc-700 transition-colors"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowCostForm('totals')}
                    disabled={loading}
                    className="mt-2 w-full px-3 py-2 border border-zinc-700 rounded-lg text-sm text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 flex items-center justify-center gap-2 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Global Cost
                  </button>
                )}

                <div className="border-t border-zinc-700 pt-3 mt-3">
                  <div className="flex justify-between items-center font-semibold">
                    <span className="text-sm text-zinc-100">Total Profit</span>
                    <span className={`text-base ${totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      ${formatNumber(totalProfit)}
                    </span>
                  </div>
                  <div className="pl-4 text-xs space-y-1 mt-1">
                    <div className="flex justify-between items-center text-zinc-600">
                      <span>Managed Profit</span>
                      <span className={profitSplit.managed >= 0 ? 'text-emerald-500' : 'text-red-400'}>
                        ${formatNumber(profitSplit.managed)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-zinc-600">
                      <span>Owned Profit</span>
                      <span className={profitSplit.owned >= 0 ? 'text-emerald-500' : 'text-red-400'}>
                        ${formatNumber(profitSplit.owned)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Property Cards */}
          {visibleSlots.map((slot) => (
            <div
              key={slot._id}
              className="bg-zinc-800 rounded-xl border border-zinc-700"
            >
              <div className="p-4 border-b border-zinc-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold text-zinc-100">{slot.propertyName}</h3>
                  <GripVertical className="w-4 h-4 text-zinc-600 cursor-move" />
                </div>
              </div>

              <div className="p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-zinc-300">Revenue</span>
                  <span className="text-sm font-medium text-zinc-100">${formatNumber(slot.revenue)}</span>
                </div>
                {(slot.allotmentItems || []).length > 0 && (
                  <div className="pl-4 space-y-1">
                    {slot.allotmentItems.map((item, i) => (
                      <div key={i} className="flex justify-between items-center text-xs text-violet-400">
                        <span className="flex items-center gap-1">
                          <span>↳ Allotment{item.notes ? ` "${item.notes}"` : ''}</span>
                          <span className="text-violet-600">({item.occurrences}×)</span>
                        </span>
                        <span>${formatNumber(item.monthlyRevenue)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-zinc-300">Cleaning Fees</span>
                  <span className="text-sm font-medium text-zinc-100">${formatNumber(slot.cleaningFees)}</span>
                </div>
                {(slot.allotmentItems || []).some(item => item.cleaningFeeTotal > 0) && (
                  <div className="pl-4 space-y-1">
                    {slot.allotmentItems.filter(item => item.cleaningFeeTotal > 0).map((item, i) => (
                      <div key={i} className="flex justify-between items-center text-xs text-violet-400">
                        <span>↳ Allotment{item.notes ? ` "${item.notes}"` : ''} cleaning</span>
                        <span>${formatNumber(item.cleaningFeeTotal)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {slot.isAdministrated && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-300">Admin Fee (10%)</span>
                    <span className="text-sm font-medium text-zinc-100">
                      ${formatNumber(Math.max(slot.revenue - slot.cleaningFees, 0) * 0.1)}
                    </span>
                  </div>
                )}
                {slot.isAdministrated && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-zinc-300">Owner Revenue</span>
                    <span className="text-sm font-medium text-zinc-100">
                      ${formatNumber(Math.max(slot.revenue - slot.cleaningFees, 0) - Math.max(slot.revenue - slot.cleaningFees, 0) * 0.1)}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={slot.isAdministrated}
                    onChange={(e) => handleAdminToggle(slot._id, e.target.checked)}
                    className="rounded border-zinc-600 text-violet-600 focus:ring-violet-500 bg-zinc-800"
                    disabled={loading}
                  />
                  <label className="text-xs text-zinc-500">Administrated</label>
                </div>

                <div className="border-t border-zinc-700 pt-3">
                  <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-2">Property Costs</h4>

                  {(slot.costs || []).map((cost, index) => (
                    <div key={`${slot._id}-${index}`} className="flex items-center gap-2 mb-2">
                      {editingCost === `${slot._id}-${index}` ? (
                        <>
                          <input
                            type="text"
                            value={cost.name}
                            onChange={(e) => {
                              const newCosts = [...slot.costs];
                              newCosts[index] = { ...newCosts[index], name: e.target.value };
                              setSlots(prev => prev.map(s =>
                                s._id === slot._id ? { ...s, costs: newCosts } : s
                              ));
                            }}
                            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-md py-1.5 px-2 text-zinc-100 text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                            disabled={loading}
                          />
                          <input
                            type="number"
                            value={cost.amount}
                            onChange={(e) => {
                              const newCosts = [...slot.costs];
                              newCosts[index] = { ...newCosts[index], amount: Number(e.target.value) };
                              const newNetIncome = recalcNetIncome(slot, newCosts);
                              setSlots(prev => prev.map(s =>
                                s._id === slot._id ? { ...s, costs: newCosts, netIncome: newNetIncome } : s
                              ));
                            }}
                            className="w-24 bg-zinc-800 border border-zinc-700 rounded-md py-1.5 px-2 text-zinc-100 text-sm focus:outline-none focus:ring-1 focus:ring-violet-500"
                            disabled={loading}
                          />
                          <button
                            onClick={() => {
                              setEditingCost(null);
                              const monthKey = cacheManager.getMonthKey(monthOffset);
                              cacheManager.updateReportSlot(monthKey, slot._id, { costs: slot.costs, netIncome: slot.netIncome });
                              fetch(`${process.env.REACT_APP_API_URL}/api/reservations/report-slots/${slot._id}`, {
                                method: 'PUT',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${localStorage.getItem('auth_token')}`
                                },
                                body: JSON.stringify({ costs: slot.costs })
                              }).then(response => {
                                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                              }).catch(err => {
                                console.error('[Reports] Error saving cost edit:', err);
                                alert('Failed to save cost edit — it may be lost on refresh.');
                              });
                            }}
                            className="text-emerald-500 hover:text-emerald-400 transition-colors"
                            disabled={loading}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm text-zinc-300 flex items-center gap-1.5">
                            {cost.name}
                            {cost.isRecurring && (
                              <RefreshCw className="w-3 h-3 text-violet-400" title="Recurring — copied to future months" />
                            )}
                          </span>
                          <span className="w-24 text-right text-sm font-medium text-zinc-300">
                            ${formatNumber(cost.amount)}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingCost(`${slot._id}-${index}`)}
                              className="text-zinc-600 hover:text-violet-400 transition-colors"
                              disabled={loading}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCost(slot._id, index)}
                              className="text-zinc-600 hover:text-red-400 transition-colors"
                              disabled={loading}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  {showCostForm === slot._id ? (
                    <div className="space-y-2 p-3 bg-zinc-950 rounded-lg border border-zinc-800 mt-2">
                      <input
                        type="text"
                        value={newCost.name}
                        onChange={(e) => setNewCost(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Cost description"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-md py-1.5 px-2 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 text-sm"
                        disabled={loading}
                      />
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          value={newCost.amount}
                          onChange={(e) => setNewCost(prev => ({ ...prev, amount: e.target.value }))}
                          placeholder="Amount"
                          className="w-28 bg-zinc-800 border border-zinc-700 rounded-md py-1.5 px-2 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 text-sm"
                          disabled={loading}
                          step="0.01"
                        />
                        <label className="flex items-center gap-1.5 text-xs text-zinc-400 whitespace-nowrap cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newCost.isRecurring}
                            onChange={(e) => setNewCost(prev => ({ ...prev, isRecurring: e.target.checked }))}
                            className="rounded border-zinc-600 text-violet-600 focus:ring-violet-500 bg-zinc-800"
                            disabled={loading}
                          />
                          Recurring
                        </label>
                        <div className="flex-1" />
                        <button
                          onClick={() => handleAddCost(slot._id, newCost)}
                          className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-sm hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                          disabled={loading || !newCost.name || !newCost.amount}
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            setShowCostForm(null);
                            setNewCost({ name: '', amount: '', isRecurring: false });
                          }}
                          className="px-3 py-1.5 bg-zinc-800 text-zinc-400 rounded-md text-sm hover:bg-zinc-700 border border-zinc-700 transition-colors"
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowCostForm(slot._id)}
                      disabled={loading}
                      className="mt-2 w-full px-3 py-2 border border-zinc-700 rounded-lg text-sm text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 flex items-center justify-center gap-2 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Cost
                    </button>
                  )}

                  <div className="border-t border-zinc-800 pt-3 mt-3">
                    <div className="flex justify-between items-center font-semibold">
                      <span className="text-sm text-zinc-200">Profit</span>
                      <span className={`text-base ${(slot.netIncome || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        ${formatNumber(slot.netIncome || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Empty state */}
          {slots.length === 0 && !loading && (
            <div className="col-span-1 md:col-span-2 lg:col-span-3 flex items-center justify-center p-12">
              <div className="text-center">
                <p className="text-zinc-500 mb-2 text-sm">No data for this month yet.</p>
                <p className="text-xs text-zinc-600">Data will be available after the next sync.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
